import {
  AfterViewInit,
  computed,
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnDestroy,
  Output,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef, RepositionScrollStrategy } from '@angular/cdk/overlay';
import { DomPortal } from '@angular/cdk/portal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FloatingRegistryService } from "@core/services/floating-registry.service";
import { FLOATING_HOST_BIND } from "@core/directives/floating/floating.constants";
import { OverlayPosition, TriggerModes, OverlayAlignment, PositionType, Display, Visibility, PointerEvents, OVERLAY_POSITIONS, TRIGGER_MODES, POSITION_TYPES, DISPLAY, VISIBILITY, POINTER_EVENTS, OVERLAY_ALIGNMENTS } from "@core/directives/floating/floating.types";
import { getHTMLElement, MaybeElementRef } from "@core/utils/html.utils";
import { filter, fromEvent, map, merge, Observable, switchMap, take, timer } from 'rxjs';

export const FLOATING_TRIGGER_ACTIVE_CLASS: string = "floating-trigger--active";
@Directive({
  selector: '[appFloating]',
  standalone: true,
  host: FLOATING_HOST_BIND
})
export class FloatingDirective implements AfterViewInit, OnDestroy {
  public readonly triggerRef: InputSignal<MaybeElementRef> = input.required<MaybeElementRef>();

  public readonly offsetRef: InputSignal<MaybeElementRef | undefined> = input<MaybeElementRef>();

  /**
   * @description Defines the direction the component will be displayed at
   *
   * @example '' <div appFloating [position]="top">Shows above the ref</div>
   *
   */
  public readonly position: InputSignal<OverlayPosition> = input<OverlayPosition>(OVERLAY_POSITIONS.right);

  public readonly triggerMode: InputSignal<TriggerModes> = input<TriggerModes>(TRIGGER_MODES.click);

  public readonly hoverCloseDelay: InputSignal<number> = input<number>(200);

  public readonly parentDestroyRef: InputSignal<DestroyRef | undefined> = input<DestroyRef>();

  public readonly single: InputSignal<boolean> = input<boolean>(true);

  /**
   * @description Defines the alignment the component will be displayed at
   * @example
   * '' <div appFloating [position]="top" [alignment]="start">Shows above the ref on the left</div>
   * '' <div appFloating [position]="left" [alignment]="start">Shows left to the ref with top alignment</div>
   */
  public readonly alignment: InputSignal<OverlayAlignment | null> = input<OverlayAlignment | null>(null);

  public readonly offsetX: InputSignal<number> = input<number>(0);

  public readonly offsetY: InputSignal<number> = input<number>(0);

  private _overlayRef: OverlayRef | null = null;

  private readonly overlay: Overlay = inject(Overlay);

  private readonly _host: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  private readonly _triggerRef: Signal<HTMLElement> = computed<HTMLElement>(() => getHTMLElement(this.triggerRef()));

  // eslint-disable-next-line @tseslint/no-non-null-assertion, @unicorn/consistent-function-scoping
  private readonly _anchorRef: Signal<HTMLElement> = computed<HTMLElement>(() => (this.offsetRef() ? getHTMLElement(this.offsetRef()!) : getHTMLElement(this.triggerRef())));

  private readonly floatingRegistry: FloatingRegistryService = inject(FloatingRegistryService);

  private readonly isHostOpen: WritableSignal<boolean> = signal(false);

  private readonly positionStyle: WritableSignal<PositionType> = signal(POSITION_TYPES.absolute);

  private readonly display: WritableSignal<Display> = signal(DISPLAY.none);

  private readonly zIndex: WritableSignal<number> = signal(1000);

  private readonly visibility: WritableSignal<Visibility> = signal(VISIBILITY.hidden);

  private readonly pointerEvents: WritableSignal<PointerEvents> = signal(POINTER_EVENTS.none);

  @Output() public readonly closed: EventEmitter<void> = new EventEmitter<void>();

  @Output() public readonly opened: EventEmitter<void> = new EventEmitter<void>();

  public ngOnDestroy (): void {
    this.closeHost();
  }

  public ngAfterViewInit (): void {
    this.parentDestroyRef()?.onDestroy(this.closeHost.bind(this));
    this.addEventListeners();
  }

  private addEventListeners (): void {
    const triggerClick: Observable<Event> = fromEvent(this._triggerRef(), "click");
    const triggerMouseenter: Observable<Event> = fromEvent(this._triggerRef(), "mouseenter");
    const triggerMouseleave: Observable<Event> = fromEvent(this._triggerRef(), "mouseleave");
    const hostMouseenter: Observable<Event> = fromEvent(this._host.nativeElement, "mouseenter");
    const hostMouseleave: Observable<Event> = fromEvent(this._host.nativeElement, "mouseleave");

    if (this.triggerMode() === TRIGGER_MODES.click) {
      triggerClick
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.openHost());
    } else {
      triggerMouseenter
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.openHost();
          this.openHost();
        });
      merge(triggerMouseleave, hostMouseleave)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .pipe(
          switchMap(() =>
            merge(
              timer(this.hoverCloseDelay()).pipe(
                map(() => ({ canClose: true }))
              ),
              merge(triggerMouseenter, hostMouseenter).pipe(
                map(() => ({ canClose: false }))
              )
            ).pipe(
              take(1),
              filter((result: { canClose: boolean; }): result is { canClose: true; } => result.canClose)
            ))
        )
        .subscribe(() => this.closeHost());
      hostMouseenter
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.openHost());
    }
  }

  private openHost (): void {
    if (this.isHostOpen()) return;

    this.single() && this.floatingRegistry.register(this);
    this._overlayRef?.detach();
    this.createOverlay();
    this.applyOpenedStyles();
    this._host.nativeElement.focus();
    this.isHostOpen.set(true);
    this.opened.emit();
  }

  public closeHost (): void {
    this._overlayRef?.detach();
    this._overlayRef?.dispose();
    this.single() && this.floatingRegistry.unregister(this);
    this.applyClosedStyles();
    this.isHostOpen.set(false);
    this.closed.emit();
  }

  private createOverlay (): void {
    const positionStrategy: FlexibleConnectedPositionStrategy = this.createPositionStrategy();
    const scrollStrategy: RepositionScrollStrategy = this.overlay.scrollStrategies.reposition();
    const hasBackdrop: boolean = this.triggerMode() === TRIGGER_MODES.click;
    const backdropClass: string = 'cdk-overlay-transparent-backdrop';

    this._overlayRef = this.overlay.create({ positionStrategy, scrollStrategy, hasBackdrop, backdropClass });
    this._overlayRef.attach(new DomPortal<HTMLElement>(this._host.nativeElement));

    if (this.triggerMode() === TRIGGER_MODES.click) {
      this._overlayRef.backdropClick()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(this.closeHost.bind(this));
    }
  }

  private applyClosedStyles (): void {
    this.positionStyle.set(POSITION_TYPES.absolute);
    this.display.set(DISPLAY.none);
    this.visibility.set(VISIBILITY.hidden);
    this.pointerEvents.set(POINTER_EVENTS.none);
    this._triggerRef().classList.remove(FLOATING_TRIGGER_ACTIVE_CLASS);
  }

  private applyOpenedStyles (): void {
    this._triggerRef().classList.add(FLOATING_TRIGGER_ACTIVE_CLASS);
    this.positionStyle.set(POSITION_TYPES.relative);
    this.display.set(DISPLAY.block);
    this.visibility.set(VISIBILITY.visible);
    this.pointerEvents.set(POINTER_EVENTS.auto);
  }

  /**
   * @Description Resolves position and alignment based on input parameters
   *
   * @returns The resolved position
   */
  private createPositionStrategy (): FlexibleConnectedPositionStrategy {
    const horizontalAlignment: "start" | "center" | "end" = this.alignment() ?? "start";

    let verticalAlignment: "top" | "center" | "bottom";

    switch (this.alignment()) {
      case OVERLAY_ALIGNMENTS.center: {
        verticalAlignment = "center";

        break;
      }

      case OVERLAY_ALIGNMENTS.end: {
        verticalAlignment = "bottom";

        break;
      }

      default: {
        verticalAlignment = "top";

        break;
      }
    }

    /* TODO: config for originX & originY to set position depending on Trigger size */
    const positions: Record<OverlayPosition, ConnectedPosition> = {
      bottom: {
        originX: horizontalAlignment, originY: 'bottom', overlayX: horizontalAlignment, overlayY: 'top', offsetX: this.offsetX(), offsetY: this.offsetY()
      },
      top: {
        originX: horizontalAlignment, originY: 'top', overlayX: horizontalAlignment, overlayY: 'bottom', offsetX: this.offsetX(), offsetY: this.offsetY()
      },
      right: {
        originX: 'end', originY: verticalAlignment, overlayX: 'start', overlayY: verticalAlignment, offsetX: this.offsetX(), offsetY: this.offsetY()
      },
      left: {
        originX: 'start', originY: verticalAlignment, overlayX: 'end', overlayY: verticalAlignment, offsetX: this.offsetX(), offsetY: this.offsetY()
      }
    };
    const primary: ConnectedPosition = positions[ this.position() ];

    let fallback: ConnectedPosition;

    switch (this.position()) {
      case OVERLAY_POSITIONS.bottom: {
        fallback = positions.top;

        break;
      }

      case OVERLAY_POSITIONS.top: {
        fallback = positions.bottom;

        break;
      }

      case OVERLAY_POSITIONS.left: {
        fallback = positions.right;

        break;
      }

      default: {
        fallback = positions.left;
      }
    }

    return this.overlay.position()
      .flexibleConnectedTo(this._anchorRef())
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([ primary, fallback ]);
  }
}
